import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  COACHING_MANTRA_DISCLAIMER,
  COACHING_MANTRA_INTENTION_LABELS,
  type CoachingMantraIntention,
} from '@mlm/shared/coaching';
import { linkButton } from '../components/panels/today';
import {
  Block,
  Chip,
  ErrorNote,
  Eyebrow,
  Loading,
  Row,
  SageCheck,
  Screen,
} from '../components/primitives';
import { MoreWithPlan, ProLock } from '../components/pro-lock';
import { coaching, type Mantra } from '../lib/api';
import { useMantras, useRefreshCoaching, useFeatureLocked } from '../lib/queries';
import { ToolFrame, toolHeading } from './tool-frame';

const intentionLabel = (i: string) =>
  COACHING_MANTRA_INTENTION_LABELS[i as CoachingMantraIntention] ?? i;

/** The mantra library. Intention first, the same shape as breathwork's outcomes. */
export function MantrasPage() {
  const locked = useFeatureLocked('mantras');
  const library = useMantras();
  const [intention, setIntention] = useState<string | null>(null);

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Mantras" title="What do you want to sit with?">
        <ProLock feature="mantras" />
      </Screen>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;

  const { mantras, intentions, proOnlyCount } = library.data;
  const offered = intentions.filter((i) => mantras.some((m) => m.intention === i));
  const shown = intention ? mantras.filter((m) => m.intention === intention) : mantras;

  return (
    <Screen
      back="/align"
      eyebrow="Mantras"
      title="What do you want to sit with?"
      subtitle="Choose a mantra and repeat it at your own pace. The counter keeps your place."
    >
      <div className="flex flex-wrap gap-2 border-b border-border px-5 py-5">
        <Chip selected={intention === null} onClick={() => setIntention(null)}>
          All
        </Chip>
        {offered.map((i) => (
          <Chip key={i} selected={intention === i} onClick={() => setIntention(i)}>
            {intentionLabel(i)}
          </Chip>
        ))}
      </div>
      {shown.map((m) => (
        <Row
          key={m.id}
          title={m.title}
          meta={`${intentionLabel(m.intention)} · ${m.repetitions} repetitions`}
          to={`/align/mantras/${encodeURIComponent(m.id)}`}
        />
      ))}
      <MoreWithPlan count={proOnlyCount} noun="mantra" />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {COACHING_MANTRA_DISCLAIMER}
      </p>
    </Screen>
  );
}

/** One mantra, with a counter. The practice is recorded when the count is reached. */
export function MantraSessionPage() {
  const { id = '' } = useParams();
  const locked = useFeatureLocked('mantras');
  const library = useMantras();

  if (locked) {
    return (
      <ToolFrame close="/align/mantras">
        <ProLock feature="mantras" />
      </ToolFrame>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;
  const mantra = library.data.mantras.find((m) => m.id === id);
  if (!mantra) {
    return (
      <ToolFrame close="/align/mantras">
        <Block
          title="This mantra is not available"
          action={
            <Link to="/align/mantras" className={linkButton}>
              Choose another
            </Link>
          }
        >
          It may have been changed or removed. Choose another mantra.
        </Block>
      </ToolFrame>
    );
  }
  return <Session mantra={mantra} />;
}

function Session({ mantra }: { mantra: Mantra }) {
  const refresh = useRefreshCoaching();
  const [count, setCount] = useState(0);
  const recorded = useRef(false);
  const done = count >= mantra.repetitions;

  const complete = useMutation({
    mutationFn: () => coaching.practice('mantra'),
    meta: { silentErrorCodes: ['coaching_pro_required'] },
    onSuccess: () => void refresh(),
  });

  // The practice counts when the round is reached, once. Tapping past the
  // target is allowed and changes nothing: some members keep going.
  useEffect(() => {
    if (!done || recorded.current) return;
    recorded.current = true;
    coaching.event('mantra_completed', { mantra: mantra.id, repetitions: mantra.repetitions });
    complete.mutate();
  }, [done, mantra, complete]);

  return (
    <ToolFrame close="/align/mantras">
      <Eyebrow className="mb-3">Mantra · {intentionLabel(mantra.intention)}</Eyebrow>
      <h1 className={toolHeading}>{mantra.title}</h1>
      {mantra.script && (
        <p className="mt-3 text-[1.35rem] leading-relaxed text-ink/80" lang="sa">
          {mantra.script}
        </p>
      )}
      <p className="mt-4 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">
        {mantra.meaning}
      </p>
      <p className="mt-3 text-sm leading-relaxed text-ink/60">{mantra.use}</p>

      <div className="mt-10 flex flex-col items-center">
        <button
          type="button"
          onClick={() => setCount((c) => c + 1)}
          aria-label={`Count one repetition. ${count} of ${mantra.repetitions} so far.`}
          className="flex h-44 w-44 flex-col items-center justify-center rounded-full border border-verde bg-wash transition-colors hover:bg-[#d2e5be] active:bg-[#c8dfb1]"
        >
          <span className="font-display text-5xl font-semibold leading-none text-ink tabular-nums">
            {count}
          </span>
          <span className="eyebrow mt-2">of {mantra.repetitions}</span>
        </button>
        <p className="eyebrow mt-5">Tap for each repetition</p>
        {count > 0 && (
          <button
            type="button"
            className="eyebrow mt-3 underline underline-offset-4 hover:text-ink"
            onClick={() => setCount(0)}
          >
            Start the count again
          </button>
        )}
      </div>

      {done && (
        <section className="mt-10 border-t border-border pt-6">
          <p className="flex items-center gap-2 text-[0.95rem] text-ink">
            <SageCheck size={20} /> Round complete
          </p>
          {complete.data && (
            <p className="eyebrow mt-3">
              Today's Success Score: {complete.data.score.total} · {complete.data.score.label}
            </p>
          )}
        </section>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-4 pt-10">
        {done ? (
          <Link to="/align" className={linkButton}>
            Back to Align
          </Link>
        ) : (
          <Link to="/align/mantras" className="eyebrow underline underline-offset-4">
            Choose another
          </Link>
        )}
      </div>
      <p className="pt-8 text-xs leading-relaxed text-muted-foreground">
        {COACHING_MANTRA_DISCLAIMER}
      </p>
    </ToolFrame>
  );
}
